// The `create` HTTP API (services/backend/src/http/create.ts): one streaming callable that creates or updates a
// world object -- a character (Animal), a mind, an item or a backdrop -- from a prompt, images and explicit values,
// using version-3 personas for characters and minds. Every part it produces is written to the database as soon as
// it exists and streamed back as an event, so a client can show progress and the document stays the source of truth.
import { z } from "zod";
import { zWorldMomentPosition, type WorldMomentPosition } from "./worldMoment";
import { zStoryScene, zStoryBeatRecord, type StoryBeatRecord, type StoryScene } from "./performances";
import { zActionCue, zSceneSetup } from "./storyActions";
import type { PlaybackIssue } from "./storyPlayback";
import { zImageReference } from "./imageReferenceSchema";
import type { ImageReference } from "./images";
import { zStorySceneLength, type StoryStreamEvent, type StreamPerformanceResponse } from "./performances";
import { zStoryFraming } from "./storyFraming";
import { zStoryResponseSource } from "./storyResponseInput";
import { zStoryAudioDelivery } from "./performanceSpeech";
import { zStoryAssetCreation } from "./storyAssets";
import { zChoreo } from "./choreo";
import { zSongPlan, type SongPlan } from "./song";
import { STORY_REFERENCE_IMAGE_LIMIT, zStoryOutline, type StoryOutline } from "./storyOutline";
import { zStoryProposal, type StoryProposal } from "./storyProposal";
import { zStoryCustomization, zCustomizationGeneration, type StoryCustomization } from "./storyCustomization";
import { zPuppetStaging } from "./storyEditor";
import { zWorldGenerationRef, type WorldSourceRef, type WorldSceneStart } from "./worlds";
import {
  MAX_SCENE_AUDIO_MS,
  MAX_SCENE_AUDIO_SINGERS,
  zSceneAudioMode,
  type SceneAudioIssue,
  type SceneAudioPhase,
} from "./sceneAudio";

export const CREATE_IMAGE_OPTIONS = [
  { id: "fast", label: "Fast", hint: "Gemini Flash Lite" },
  { id: "best", label: "Best", hint: "Gemini Flash" },
  { id: "pro", label: "Pro", hint: "Gemini Pro with reasoning" },
  { id: "openai", label: "OpenAI", hint: "GPT Image 2.5 Sunburst" },
] as const;
export type CreateImageQuality = (typeof CREATE_IMAGE_OPTIONS)[number]["id"];
export type CreateSheetResolution = "1K" | "2K";
export const OPENAI_WORLD_IMAGE_MODEL = "gpt-image-2.5-sunburst";
export const GEMINI_WORLD_PRO_IMAGE_MODEL = "gemini-3-pro-image";

/** B is conventionally used by legacy outfit sheets, so additional poses start at C. */
export const CREATE_SHEET_KEYS = "ACDEFGHIJKLMNOPQRSTUVWXYZB".split("");
export function nextCreateSheetKey(existing: readonly string[]): string | undefined {
  return CREATE_SHEET_KEYS.find((key) => !existing.includes(key));
}

const id = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-zA-Z0-9_-]+$/);
/** Backdrop slugs and variation names: lower-case words joined by dashes. */
export const zCreateSlug = z
  .string()
  .min(1)
  .max(60)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

export const zCreateKind = z.enum([
  "character",
  "mind",
  "item",
  "backdrop",
  "performance",
  "scene",
  "story",
  "worldAdvice",
]);
export type CreateKind = z.infer<typeof zCreateKind>;

/**
 * The parts of an object a request may produce or change. On create, an omitted scope means every part the kind
 * has (see `defaultCreateScope`); on update, an omitted scope is inferred from the prompt and values.
 *   look        character/mind: the reference portrait. item: its transparent artwork. backdrop: its base art.
 *   details     character/mind: name, appearance, personality, backstory, traits... item: name, description...
 *               backdrop: description.
 *   poses       character/mind: expression sheets (the A sheet and/or custom sheets).
 *   variations  item and backdrop: named alternative pictures of the same thing (open/closed, night...).
 */
export const zCreateScope = z.enum(["look", "details", "poses", "variations"]);
export type CreateScope = z.infer<typeof zCreateScope>;

export const CREATE_SCOPES_BY_KIND: Record<CreateKind, readonly CreateScope[]> = {
  character: ["look", "details", "poses"],
  mind: ["look", "details", "poses"],
  item: ["look", "details", "variations"],
  backdrop: ["look", "details", "variations"],
  performance: [],
  scene: [],
  story: [],
  worldAdvice: [],
};

/** What a create request with no scope produces. */
export function defaultCreateScope(kind: CreateKind): CreateScope[] {
  if (kind === "performance" || kind === "scene" || kind === "story" || kind === "worldAdvice") return [];
  return kind === "character" || kind === "mind" ? ["look", "details", "poses"] : ["look", "details"];
}

/** An object in the world whose look is used as an input image. */
export const zCreateSourceRef = z
  .object({
    kind: zCreateKind.exclude(["performance", "scene", "story", "worldAdvice"]),
    /** Animal / Mind / Item id, or backdrop slug. */
    id,
    /** character/mind: which persona (default persona when omitted). */
    personaId: id.optional(),
    /** item/backdrop: a named variation instead of the base picture. */
    variant: z.string().max(60).optional(),
    /** backdrop: the story it belongs to (the request's story when omitted). */
    storyId: id.optional(),
  })
  .strict();
export type CreateSourceRef = z.infer<typeof zCreateSourceRef>;

/**
 * An input image: an uploaded picture (https URL, e.g. from `uploadImage`) or an object already in the world.
 *   subject  this is (a picture of) the thing to create or change -- drawn to look like it.
 *   style    match its art style.
 *   context  for reference only (a place, a companion, a prop to hold...).
 */
export const zCreateImageInput = z
  .object({
    url: z.string().url().max(2000).optional(),
    image: zImageReference.optional(),
    source: zCreateSourceRef.optional(),
    role: z.enum(["subject", "style", "context"]).default("subject"),
    /** Short description the model is given with the image ("her grandma's hat"). */
    label: z.string().max(200).optional(),
  })
  .strict()
  .refine((i) => [i.url, i.source, i.image].filter(Boolean).length === 1, {
    message: "An image needs exactly one of image, url or source",
  });
export type CreateImageInput = z.input<typeof zCreateImageInput>;

const trait = z.object({ name: z.string().min(1).max(200), description: z.string().max(1000).optional() });

/**
 * Values written as given, without a model (rename, edit a backstory, revert a look...). They win over anything
 * generated in the same request. Each kind uses the fields that apply to it and rejects the rest.
 */
export const zCreateValues = z
  .object({
    // character / mind / item
    name: z.string().min(1).max(200),
    // character / mind
    appearance: z.string().max(4000),
    personality: z.string().max(4000),
    backstory: z.string().max(8000),
    age: z.string().max(100),
    gender: z.string().max(200),
    isHuman: z.boolean(),
    abilities: z.array(trait).max(12),
    weaknesses: z.array(trait).max(12),
    // item / backdrop
    description: z.string().max(4000),
    // item
    itemKind: z.enum(["prop", "wearable", "scene"]),
    slot: z.enum(["head", "body", "accessory"]),
    interactionLabel: z.string().max(80),
    /** Replace the look with this existing picture (an earlier look, an upload) instead of generating one. */
    look: z.object({
      /** Full reference for current clients; url remains accepted for older callers. */
      image: zImageReference.optional(),
      url: z.string().url().max(2000).optional(),
    }).strict().refine((look) => !!look.image !== !!look.url, {
      message: "A look needs exactly one of image or url",
    }),
  })
  .partial()
  .strict();
export type CreateValues = z.infer<typeof zCreateValues>;

/**
 * An expression sheet to draw. "A" is the standard 16-pose identity sheet, the only letter with a fixed meaning (the
 * contract): it takes no poses and is never a performance sheet. Every other letter is a custom sheet, and what goes
 * where is convention: B is the outfit showcase, and a story's or gram's new performer gets its performance sheet on C
 * (nextCreateSheetKey), but nothing requires either.
 */
export const zCreateSheetSpec = z
  .object({
    key: z.string().regex(/^[A-Z]$/),
    /** Foreground is the default for new sheets; redraws retain saved framing unless explicitly overridden. */
    framing: z.enum(["foreground", "full-character"]).optional(),
    /** Legacy structured pose instructions. Free-form editor prompts use posePrompt instead. */
    poses: z.array(z.string().min(1).max(300)).max(16).optional(),
    /** Describe custom poses in ordinary prose, a list, or a table; AI extracts the panel instructions. */
    posePrompt: z.string().min(1).max(8000).optional(),
    /** Extra direction for this sheet only. */
    prompt: z.string().max(2000).optional(),
    /** false: only add a missing sheet. true: deliberately regenerate this letter. Omitted retains explicit-key compatibility. */
    replace: z.boolean().optional(),
    /** Upgrade the current sheet to 2K and apply it if still current; preserve both versions in image history. */
    upgrade: z.boolean().optional(),
    /** Refuse an upgrade if the current sheet changed since the user selected it. */
    expectedImageId: id.optional(),
    /**
     * Optional description, saved with the sheet: "performance" means its poses were designed for one performance
     * (panel 1 the idle, 16 the finale, by convention). It gates nothing: any sheet drawn from its own poses (or
     * posePrompt) stores them as posePlan, so a later redraw with no poses draws the same 16 again, marked or not.
     * Never on the A sheet.
     */
    purpose: z.enum(["performance"]).optional(),
    /**
     * Reusable pose sequences over this sheet's own panels ("C3" on C, "A3" on A), on any sheet whatever its letter
     * or purpose; saved with it and kept while its panels keep their meaning (a redraw with no new poses), replaced
     * by a redraw with new ones. Each needs an id, unique on the sheet, which a pose action's `choreo` names.
     */
    choreos: z.array(zChoreo).max(8).optional(),
  })
  .strict();
export type CreateSheetSpec = z.infer<typeof zCreateSheetSpec>;

export const zCreateVariationSpec = z
  .object({
    name: zCreateSlug,
    /** What differs in this variation ("the door is open", "at night, lanterns lit"). */
    prompt: z.string().min(1).max(1000),
  })
  .strict();
export type CreateVariationSpec = z.infer<typeof zCreateVariationSpec>;

export const zCreateOptions = z
  .object({
    /** Fast/Best: Gemini pipelines. Pro: Gemini Pro with native reasoning. OpenAI: GPT Image. */
    quality: z.enum(["fast", "best", "pro", "openai"]),
    /** Native sheet generation resolution. 2K uses Gemini Flash when Fast is selected (Lite only supports 1K). */
    sheetResolution: z.enum(["1K", "2K"]),
    /**
     * character/mind update: write the result as a new persona (keeping the old one) and make it the default,
     * instead of changing the persona in place. Defaults to in place for version-3 personas and to a new persona
     * for older ones, which are never modified.
     */
    newPersona: z.boolean(),
    /** A story-specific alternate leaves the character's default and other story bindings intact. */
    storyOnlyPersona: z.boolean(),
    /** Add the object to the story's world (cast, backdrops, items). Default true when `storyId` is set. */
    addToStory: z.boolean(),
    /** Create hidden (a wizard draft), or on update show/hide the object. */
    hidden: z.boolean(),
    /** look: start over from the prompt and images, ignoring the current look. */
    fresh: z.boolean(),
    /**
     * backdrop look: how the waist-up puppets are hidden (zPuppetStaging; default "bottom-stage"). On an update with
     * scope ["look"] and no prompt, the current art is edited to show it ("make this place puppet-ready").
     */
    staging: zPuppetStaging,
  })
  .partial()
  .strict();
export type CreateOptions = z.infer<typeof zCreateOptions>;

/**
 * kind "scene": write, revise or score one scene of a story (docs/scene-audio.md). It never has a writer of its own:
 *   create  a new scene continuing the story after `sourceSceneId` (default: the branch head), written from `prompt`
 *           by the same story generator as streamPerformance; its events arrive in `performance` envelopes.
 *   adapt   a revision of `sourceSceneId` written from `prompt` (the original stays playable, see revisionOf).
 *   score   keep `sourceSceneId`'s beats and build its synchronized audio timeline from `music`: a durable job whose
 *           phases arrive as `step` events. A dropped connection never cancels it; send the same requestId to
 *           reattach. The timeline is attached as `scene.audio`, archiving any previous one.
 * Available now: create and adapt without music, and score with an uploaded recording (musical or music-video).
 * Generated music, background scores and musical create/adapt (writing lyrics, then scoring) are not yet.
 */
export const zCreateSceneSpec = z
  .object({
    operation: z.enum(["create", "adapt", "score", "reviseDraft"]),
    /** Transient editor/preview input. reviseDraft never persists story content. */
    draft: z
      .object({
        scene: zStoryScene.extend({ setup: zSceneSetup.innerType().optional() }),
        beats: z.array(z.object({ sequence: z.number().int().nonnegative(), beat: zActionCue.innerType() })).max(400),
      })
      .optional(),
    diagnostics: z
      .array(
        z.object({
          message: z.string().max(2000),
          severity: z.enum(["error", "warning"]),
          beatId: z.string().optional(),
          cueIndex: z.number().optional(),
          recommendation: z.string().max(2000).optional(),
        })
      )
      .max(200)
      .optional(),
    /** Whole-story customization intentionally rewrites delivery and regenerates speech. */
    customizing: z.boolean().optional(),
    /** Earlier reviewed drafts in this customization pass; read-only continuity, never a content commit. */
    customizationDrafts: z
      .array(z.object({ scene: zStoryScene, beats: z.array(zStoryBeatRecord).max(400) }))
      .max(100)
      .optional(),
    /** create: continue after this scene (default: the branch head). adapt / score: the scene to change. */
    sourceSceneId: id.optional(),
    /** create: the branch to continue (default: the story's default branch). */
    branchId: id.optional(),
    /** create / adapt: how long the written scene should be. */
    length: zStorySceneLength.optional(),
    /** score: the `scene.audio` revision this replaces, or "none". A newer timeline is a conflict. */
    expectedRevision: id.optional(),
  })
  .strict();
export type CreateSceneSpec = z.infer<typeof zCreateSceneSpec>;

export const zCreateStorySpec = z
  .object({
    /**
     * outline  write an outline from the idea in `prompt`, or revise `outline` with the change in `prompt`. Writes nothing.
     * build    make the story: a new story (or `storyId`'s), the outline's existing characters and objects join it, new
     *          ones are drawn, and the song's singers are mapped to the cast. Entries already built are kept.
     */
    operation: z.enum(["outline", "build", "propose", "realize", "customizePlan"]),
    customization: zStoryCustomization.optional(),
    /** customizePlan: explicit private model preferences; an empty object restores automatic choices. */
    customizationGeneration: zCustomizationGeneration.optional(),
    proposal: zStoryProposal.optional(),
    outline: zStoryOutline.optional(),
    /** outline: how long the song should be; its bars are fitted to it. */
    targetDurationMs: z.number().int().min(20_000).max(240_000).optional(),
  })
  .strict();
export type CreateStorySpec = z.infer<typeof zCreateStorySpec>;

export const zCreateSceneMusicSource = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("upload"),
      /** Storage path of an uploaded recording under Account/{accountId}/Performance/{storyId}/audio/. */
      path: z.string().min(1).max(1000),
      originalName: z.string().max(300).optional(),
      /** Written lyrics, one line per sung phrase. Musicals default to the scene's spoken lines. */
      lyrics: z.string().max(20000).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("generate"),
      /** What the song is about, beyond the request's prompt (or, for score, beyond the scene itself). */
      brief: z.string().max(4000).optional(),
      /** A preset (SONG_STYLE_PRESETS) or a description of the sound; descriptions are rewritten without names. */
      style: z
        .object({
          presetId: z.string().max(40).optional(),
          description: z.string().max(600).optional(),
          bpm: z.number().int().min(60).max(200).optional(),
        })
        .strict()
        .optional(),
      /** A reviewed song plan to perform as written (vocalists name cast ids); otherwise the songwriter writes one. */
      song: zSongPlan.optional(),
      /** The music model (default lyria-3.5). */
      model: z.enum(["lyria-3.5", "lyria-3-pro-preview"]).optional(),
      /** `song`, already recorded under the story's audio folder (a story build records it while drawing the cast). */
      recording: z
        .object({
          path: z.string().min(1).max(1000),
          model: z.string().max(100),
          url: z.string().max(2000).optional(),
          /** The recording already listened to (a scene audio analysis artifact of this story). */
          analysisPath: z.string().min(1).max(1000).optional(),
        })
        .strict()
        .optional(),
    })
    .strict(),
]);
export type CreateSceneMusicSource = z.infer<typeof zCreateSceneMusicSource>;

export const zCreateSceneMusic = z
  .object({
    mode: zSceneAudioMode,
    source: zCreateSceneMusicSource,
    targetDurationMs: z.number().int().min(5000).max(MAX_SCENE_AUDIO_MS).optional(),
    /**
     * At most two singers. With an upload, list them in the order they first sing (Vocalist A, then B) or set
     * `vocalist` explicitly. An explicit empty array leaves vocals offstage; omission selects scene speakers.
     */
    singers: z
      .array(z.object({ characterId: id, vocalist: z.enum(["A", "B"]).optional() }).strict())
      .max(MAX_SCENE_AUDIO_SINGERS)
      .optional(),
  })
  .strict();
export type CreateSceneMusic = z.infer<typeof zCreateSceneMusic>;

export const zCreateRequest = z
  .object({
    /** Client-generated; every event of this request carries it. */
    requestId: z.string().regex(/^[a-zA-Z0-9_-]{8,100}$/),
    accountId: id,
    kind: zCreateKind,
    /** Update this object; create a new one when omitted. Animal / Mind / Item id, or backdrop slug. */
    id: id.optional(),
    /** character/mind update: the persona to change (the default persona when omitted). */
    personaId: id.optional(),
    /** The story whose world this belongs to. Required for backdrops. */
    storyId: id.optional(),
    /** Private authoring draft that retains this generation's request and receipt. */
    authoringDraftId: id.optional(),
    /** Server-resolved world reference for story generation and management of its account-owned artwork. */
    world: zWorldGenerationRef.optional(),
    /** A private question or start selection. It never changes a world or launches a story. */
    worldAdvice: z
      .object({
        operation: z.enum(["answer", "chooseStart", "chat", "resolveTime"]),
        entityIds: z.array(id).max(40).optional(),
        position: zWorldMomentPosition.optional(),
        characterId: id.optional(),
        expectedRevision: z.number().int().nonnegative().optional(),
        messages: z
          .array(z.object({ role: z.enum(["user", "assistant"]), text: z.string().min(1).max(4000) }).strict())
          .max(16)
          .optional(),
      })
      .strict()
      .optional(),
    /** character create: the mind (profile) it belongs to. */
    mindId: id.optional(),
    /** What to create, or what to change. */
    prompt: z.string().max(4000).optional(),
    /** A story's reference library; other kinds take at most CREATE_REQUEST_IMAGE_LIMIT (checked by the server). */
    images: z.array(zCreateImageInput).max(STORY_REFERENCE_IMAGE_LIMIT).optional(),
    scope: z.array(zCreateScope).max(4).optional(),
    values: zCreateValues.optional(),
    /** poses: the sheets to draw (the next unused letter when omitted). Existing explicit letters regenerate. */
    sheets: z.array(zCreateSheetSpec).max(4).optional(),
    /** variations: the variations to draw. */
    variations: z.array(zCreateVariationSpec).max(8).optional(),
    options: zCreateOptions.optional(),
    /** Performance composition or fresh backdrop art. Omitted: responsive; edits preserve existing artwork dimensions. */
    framing: zStoryFraming.optional(),
    /** Performance streaming: omitted/off is silent; wait gates each spoken beat, async sends clips later. */
    audioDelivery: zStoryAudioDelivery.optional(),
    /** Transport capability only: older players accept one waiting transition per scene. */
    responseTransitionPhases: z.enum(["single", "asking-and-reply"]).optional(),
    /** Transport capability only: accept scene display titles while playable content is streaming. */
    sceneTitleUpdates: z.boolean().optional(),
    /**
     * kind performance, or kind scene create / adapt: what the story generator may create for the scene (new pose
     * sheets, characters, items, backdrops, variations), each kind with its own limit.
     */
    assetCreation: zStoryAssetCreation.optional(),
    /** kind scene: the operation and the scene it applies to. `prompt` is the brief or the change. */
    scene: zCreateSceneSpec.optional(),
    /** kind scene: the audio timeline to build. */
    music: zCreateSceneMusic.optional(),
    /** kind story: shape an outline from an idea (`prompt`), or build a story from an approved outline. */
    story: zCreateStorySpec.optional(),
    /** Continue a saved response invitation. The server reads its author direction from this exact beat. */
    interaction: z
      .object({
        sceneId: id,
        beatId: id,
        answer: z
          .string()
          .min(1)
          .max(2000)
          .refine((answer) => !!answer.trim(), "A response cannot be blank."),
        /** Suggestion requires an exact saved choice. Textbox edits are always custom. */
        source: zStoryResponseSource.optional(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .superRefine((request, ctx) => {
    if (request.kind !== "worldAdvice") {
      if (request.worldAdvice)
        ctx.addIssue({ code: "custom", path: ["worldAdvice"], message: "worldAdvice requires kind worldAdvice." });
      return;
    }
    if (!request.world || !request.worldAdvice || !request.prompt?.trim())
      ctx.addIssue({ code: "custom", message: "World advice needs a world, operation and question or purpose." });
    if (request.worldAdvice?.operation === "chat" && request.worldAdvice.expectedRevision === undefined)
      ctx.addIssue({ code: "custom", message: "World chat needs the inspected revision." });
    if (
      request.worldAdvice?.operation !== "chat" &&
      (request.worldAdvice?.position || request.worldAdvice?.characterId || request.worldAdvice?.messages)
    )
      ctx.addIssue({ code: "custom", message: "Time and character perspectives require world chat." });
    if (request.world?.ownerAccountId !== request.accountId)
      ctx.addIssue({ code: "custom", path: ["world"], message: "World advice uses the world's owning account." });
    if (
      request.world?.remember === true ||
      request.world?.anchorDescription ||
      request.world?.timelineStart ||
      request.world?.profileOverrides ||
      request.world?.selectedEntityIds
    )
      ctx.addIssue({
        code: "custom",
        path: ["world"],
        message: "World advice cannot change an anchor, style or history; use worldAdvice.entityIds for retrieval.",
      });
    const allowed = new Set(["requestId", "accountId", "kind", "world", "worldAdvice", "prompt"]);
    for (const key of Object.keys(request))
      if (!allowed.has(key))
        ctx.addIssue({ code: "custom", path: [key], message: "This field does not apply to world advice." });
  });
export type CreateRequest = z.input<typeof zCreateRequest>;
export type ParsedCreateRequest = z.infer<typeof zCreateRequest>;

export type CreateStep =
  | "inputs"
  | "scope"
  | "look"
  | "spec"
  | "details"
  | "poses"
  | "variations"
  | "save"
  | "story"
  | SceneAudioPhase;

/** A picture the client can show right away; `image` is set once it is stored. */
export interface CreateImagePayload {
  url: string;
  width?: number;
  height?: number;
  image?: ImageReference;
}

interface CreateEventBase {
  requestId: string;
  operationId?: string;
}

/** The object being created or changed, sent first so the client can subscribe to it. */
export interface CreateTargetEvent extends CreateEventBase {
  type: "target";
  kind: CreateKind;
  id: string;
  personaId?: string;
  /** Firestore path of the document holding it (the Performance for a backdrop). */
  path: string;
  created: boolean;
  scope: CreateScope[];
}

export interface CreateStepEvent extends CreateEventBase {
  type: "step";
  step: CreateStep;
  state: "started" | "done" | "failed" | "skipped";
  /** The sheet letter or variation name for poses / variations steps. */
  key?: string;
  ms?: number;
  model?: string;
  message?: string;
}

export interface CreateImageEvent extends CreateEventBase {
  type: "image";
  slot: "look" | "sheet" | "variation";
  /** Sheet letter or variation name. */
  key?: string;
  image: CreateImagePayload;
}

export interface CreateDetailsEvent extends CreateEventBase {
  type: "details";
  values: Partial<CreateValues>;
}

export interface CreateCompleteEvent extends CreateEventBase {
  type: "complete";
  result: CreateResponse;
}

export interface CreateErrorEvent extends CreateEventBase {
  type: "error";
  message: string;
  step?: CreateStep;
}

/** kind scene with generated music: the song plan, as soon as it is written. */
export interface CreateSongEvent extends CreateEventBase {
  type: "song";
  song: SongPlan;
}

/** kind story: the outline as it is written (outline) or as entries are built (build). */
export interface CreateOutlineEvent extends CreateEventBase {
  type: "outline";
  outline: StoryOutline;
}

/** kind story build: an event of the create request that makes one outline entry (`key` is the entry's id). */
export interface CreateNestedEvent extends CreateEventBase {
  type: "nested";
  key: string;
  event: CreateStreamEvent;
}

export type CreateStreamEvent =
  | CreateSongEvent
  | CreateOutlineEvent
  | CreateNestedEvent
  | CreateTargetEvent
  | CreateStepEvent
  | CreateImageEvent
  | CreateDetailsEvent
  | CreateCompleteEvent
  | CreateErrorEvent
  | (CreateEventBase & { type: "worldAdvice"; advice: WorldAdviceResult })
  | (CreateEventBase & { type: "performance"; event: StoryStreamEvent });

export interface CreateResponse {
  customization?: StoryCustomization;
  customizationVersion?: string;
  sceneDraft?: { scene: StoryScene; beats: StoryBeatRecord[]; issues: PlaybackIssue[]; recommendations: string[] };
  playbackIssues?: PlaybackIssue[];
  proposal?: StoryProposal;
  requestId: string;
  operationId?: string;
  usageTotals?: import("./aiUsage").AiUsageTotals;
  kind: CreateKind;
  id: string;
  personaId?: string;
  path: string;
  created: boolean;
  /** The parts this request produced or changed. */
  scope: CreateScope[];
  /** Parts that failed; the rest were saved. */
  failed: CreateStep[];
  warnings: string[];
  timingsMs: Partial<Record<CreateStep | "total", number>>;
  costUsd: number;
  /** A generated immutable story fork, present for kind performance. */
  performance?: StreamPerformanceResponse;
  /** kind scene: the job and the timeline it attached. */
  sceneAudio?: CreateSceneAudioResult;
  /** kind scene with generated music: the song that was performed. */
  song?: SongPlan;
  /** kind story: the outline (after build, with the story's cast, backdrops and items filled in). */
  outline?: StoryOutline;
  /** Private, transient advice; no shared world document is created. */
  worldAdvice?: WorldAdviceResult;
}

export interface WorldAdviceResult {
  operation: "answer" | "chooseStart" | "chat" | "resolveTime";
  timeResolution?: {
    status: "resolved" | "ambiguous" | "unknown";
    candidates: Array<{ description: string; start: WorldSceneStart }>;
  };
  position?: WorldMomentPosition;
  characterId?: string;
  worldId: string;
  revision: number;
  historyId: string;
  answer: string;
  claims: Array<{ id: string; text: string; certainty: "established" | "inferred" | "unknown" }>;
  /** Exact accepted snapshot and optional immutable source supporting each displayed claim. */
  citations: Array<{ claimId: string; snapshotId: string; source?: WorldSourceRef }>;
  unknowns: string[];
  /** An existing available opportunity; the caller must explicitly launch it. */
  opportunityId?: string;
}

export interface CreateSceneAudioResult {
  jobId: string;
  sceneId: string;
  /** The attached `scene.audio` revision. */
  revision: string;
  status: "draft" | "ready";
  durationMs: number;
  cueCount: number;
  issues: SceneAudioIssue[];
  /** The accounting operation of the job run that attached it (a child of the request's operation). */
  operationId?: string;
}
